/**
 * Local store. IndexedDB holds everything: encrypted blobs, wrapped keys, item
 * metadata and an oplog for sync. Nothing here needs the network, which is the
 * point — the expiry must hold in a loft with no signal.
 */

import { generateContentKey, generateMasterKey, seal, open, wrapContentKey,
         unwrapContentKey, pad, unpad } from '@mayfly/core';
import type { PresetId } from '@mayfly/core';

const DB = 'mayfly';
const VERSION = 1;

export interface LocalItem {
  itemId: string;
  preset: PresetId;
  label: string;
  createdAt: number;
  expiresAt: number | null;
  tagged: boolean;
  state: 'live' | 'expired';
  /** Bytes as stored: padded, then encrypted. */
  iv: Uint8Array;
  bytes: Uint8Array;
  width: number;
  height: number;
  previewIv: Uint8Array;
  previewBytes: Uint8Array;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function idb(): Promise<IDBDatabase> {
  dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      const items = db.createObjectStore('items', { keyPath: 'itemId' });
      items.createIndex('byExpiry', 'expiresAt');
      db.createObjectStore('keys');       // itemId -> wrapped content key
      db.createObjectStore('meta');       // 'masterKey', 'clock', 'onboarded'
      db.createObjectStore('oplog', { keyPath: 'seq', autoIncrement: true });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return idb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

/* ---------------------------------------------------------------- master key */

/**
 * The master key never leaves the device. It is created on first run with no
 * account, no email and no network call — a persona in discovery said she would
 * close any app that asked her to sign in while standing in a car park, and she
 * was right.
 */
export async function masterKey(): Promise<CryptoKey> {
  const existing = await tx<CryptoKey | undefined>('meta', 'readonly', (s) => s.get('masterKey'));
  if (existing) return existing;
  const key = await generateMasterKey();
  // Stored as a non-extractable CryptoKey: structured clone keeps it opaque, so
  // even a script running in this origin cannot read the raw bytes back out.
  await tx('meta', 'readwrite', (s) => s.put(key, 'masterKey'));
  return key;
}

/* --------------------------------------------------------------------- items */

export function newId(): string {
  // Time-ordered so the drawer sorts newest-first without a separate index.
  const now = Date.now().toString(36).padStart(9, '0');
  const rand = [...crypto.getRandomValues(new Uint8Array(8))]
    .map((b) => b.toString(36).padStart(2, '0')).join('');
  return `${now}${rand}`;
}

export async function putItem(item: LocalItem, contentKey: CryptoKey): Promise<void> {
  const wrapped = await wrapContentKey(await masterKey(), contentKey);
  await tx('items', 'readwrite', (s) => s.put(item));
  await tx('keys', 'readwrite', (s) => s.put(wrapped, item.itemId));
  await tx('oplog', 'readwrite', (s) => s.add({ op: 'put', itemId: item.itemId, at: Date.now() }));
}

export async function saveCapture(
  plain: Uint8Array,
  meta: { preset: PresetId; expiresAt: number | null; width: number; height: number; label?: string; tagged: boolean; preview?: Uint8Array },
): Promise<LocalItem> {
  const ck = await generateContentKey();
  // Pad before sealing so the stored length reveals nothing about the subject.
  const blob = await seal(ck, pad(plain));
  // The preview shares the content key deliberately: destroying that one key has
  // to take the thumbnail with it, or the drawer would still show what was deleted.
  const preview = await seal(ck, pad(meta.preview ?? new Uint8Array()));
  const item: LocalItem = {
    itemId: newId(),
    preset: meta.preset,
    label: meta.label ?? '',
    createdAt: Date.now(),
    expiresAt: meta.expiresAt,
    tagged: meta.tagged,
    state: 'live',
    iv: blob.iv,
    bytes: blob.ciphertext,
    width: meta.width,
    height: meta.height,
    previewIv: preview.iv,
    previewBytes: preview.ciphertext,
  };
  await putItem(item, ck);
  return item;
}

export async function listItems(): Promise<LocalItem[]> {
  const all = await tx<LocalItem[]>('items', 'readonly', (s) => s.getAll());
  return all.sort((a, b) => b.createdAt - a.createdAt);
}

export async function getItem(itemId: string): Promise<LocalItem | undefined> {
  return tx<LocalItem | undefined>('items', 'readonly', (s) => s.get(itemId));
}

export async function updateItem(item: LocalItem): Promise<void> {
  await tx('items', 'readwrite', (s) => s.put(item));
  await tx('oplog', 'readwrite', (s) => s.add({ op: 'patch', itemId: item.itemId, at: Date.now() }));
}

/**
 * Reveals a photo. Callers must check the decryption gate first — this function
 * only knows how to unwrap, not whether it should.
 */
export async function reveal(item: LocalItem): Promise<Uint8Array> {
  const wrapped = await tx<Uint8Array | undefined>('keys', 'readonly', (s) => s.get(item.itemId));
  if (!wrapped) throw new Error('The key for this photo has been destroyed');
  const ck = await unwrapContentKey(await masterKey(), wrapped);
  return unpad(await open(ck, { iv: item.iv, ciphertext: item.bytes }));
}

/** Decrypts just the small preview, for rendering the drawer. */
export async function revealPreview(item: LocalItem): Promise<Uint8Array | null> {
  const wrapped = await tx<Uint8Array | undefined>('keys', 'readonly', (s) => s.get(item.itemId));
  if (!wrapped) return null;
  const ck = await unwrapContentKey(await masterKey(), wrapped);
  const bytes = unpad(await open(ck, { iv: item.previewIv, ciphertext: item.previewBytes }));
  return bytes.length ? bytes : null;
}

/**
 * Crypto-shredding, locally. The key goes first: after that line the bytes are
 * unreadable whether or not the rest of this function completes.
 */
export async function destroy(itemId: string): Promise<void> {
  await tx('keys', 'readwrite', (s) => s.delete(itemId));
  await tx('items', 'readwrite', (s) => s.delete(itemId));
  await tx('oplog', 'readwrite', (s) => s.add({ op: 'destroy', itemId, at: Date.now() }));
}

/** Sweeps anything already past its expiry. Runs on every launch and on focus. */
export async function sweepExpired(nowMs: number): Promise<string[]> {
  const gone: string[] = [];
  for (const item of await listItems()) {
    if (item.expiresAt !== null && nowMs >= item.expiresAt) {
      await destroy(item.itemId);
      gone.push(item.itemId);
    }
  }
  return gone;
}

export async function meta<T>(key: string): Promise<T | undefined> {
  return tx<T | undefined>('meta', 'readonly', (s) => s.get(key));
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await tx('meta', 'readwrite', (s) => s.put(value, key));
}
